// The `views` namespace (spec 16 §4, D-305 ruling 1): the saved-views
// switcher, its Save as new form, Manage, the apply-only list and the column
// chooser, shared by every list that adopts saved views. Wired into
// apps/web's `dictionaries` (src/i18n-dictionaries.ts), so the key-parity
// gate (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it, and declared
// in src/i18n-completeness.ts, so the value gate knows ru is real.
//
// `viewsEn` is authoritative; `ViewsDict = Translated<typeof viewsEn>` makes
// ru/zh/fr parity-checked at compile time. ru is a real translation
// (views.ru.ts): the readers are the KazKabel sales managers and planners who
// already work the sales books and the work-order list in Russian. zh/fr are
// English placeholders, stated in i18n-completeness.ts rather than implied
// (D-122).
//
// WHAT IS NOT IN HERE. A member's view name is stored data, the words its
// author typed, and it reads the same in every locale (D-305 ruling 29). The
// three work-order system views carry i18n keys as their names, and those
// keys live in the production namespace (`production.list.chips.*`), where
// the chips they replace already kept them (ruling 11). The four E2-1 system
// views (D-314 ruling 7) live in the sales namespace the same way:
// `sales.tasks.views.myDay` and `.overdue`, `sales.pipeline.views.rotting`
// and `.needsNextStep`.
//
// THE WORDS. "View" is the saved thing; "list" is the screen it belongs to.
// A notice about a filter says "filters", because that is what the reader
// set. A fenced item has no words here at all: the fence drops it in
// silence, and no string may name one (D-243 ruling 3, spec 16 §3.12 item 7).

import type { Translated } from "../../../i18n-shape";
import { viewsRu as viewsRuDict } from "./views.ru";

export const viewsEn = {
  switcher: {
    label: "Saved views",
    all: "All",
    moreViews: "More views",
    modified: "Modified",
    empty: "No saved views yet",
    loading: "Loading saved views",
  },
  // The Shared group's label: the views other members shared (§4). The same
  // word heads them in the chip row, in More views and in Manage.
  shared: "Shared",
  tools: {
    columns: "Columns",
    columnsUnavailable: "Columns can be chosen once the list shows rows.",
    save: "Save",
    saving: "Saving…",
    saveAsNew: "Save as new",
    reset: "Reset",
    manage: "Manage",
  },
  notice: {
    stale: "Some filters in this view no longer apply",
    gone: "This view no longer exists",
  },
  error: {
    read: "Saved views could not be loaded.",
    readNamed: "Saved views could not be loaded, so the view in this link was not applied.",
    // A views read paused for want of a network settles as a failed read
    // (R2, PR-E4 wave 4): the row says why, and Retry stays beside it.
    offline: "You are offline, so saved views could not be loaded.",
    offlineNamed: "You are offline, so the view in this link was not applied.",
    retry: "Retry",
    retrying: "Retrying…",
    requestId: "Request id",
  },
  saveNew: {
    title: "Save as a new view",
    name: "Name",
    share: "Share with everyone who can open this list",
    submit: "Save view",
    submitting: "Saving…",
    cancel: "Cancel",
    openManage: "Open Manage",
  },
  errors: {
    nameRequired: "Give the view a name.",
    nameTooLong: "A name can be at most 60 characters.",
    nameInvalid: "This name contains a character that cannot be stored.",
    nameTaken: "Another view on this list already has this name.",
    limitMember:
      "You already have {{limit}} views on this list. Delete one in Manage to save another.",
    limitShared:
      "This list already has {{limit}} shared views. Delete one in Manage, or save this view without sharing it.",
    systemImmutable: "A built-in view cannot be changed or deleted.",
    notOwner: "Only the person who saved this view can change it.",
    notFound: "This view no longer exists.",
    readOnly: "This session is read-only, so views cannot be changed.",
    forbidden: "Views on this list cannot be saved with your access.",
    failed: "The change could not be saved.",
    // A stored filter the server refused, named by `views.filters` (R7).
    filterInvalid:
      "The “{{filter}}” filter holds a value that cannot be saved. Change or clear it, and save again.",
    // A write made offline is refused before it is sent, and nothing is
    // queued (W3, PR-E4 wave 5).
    offline: "You are offline, so the change was not saved. Try again once you are back online.",
    // A write that left the browser and got no answer back, or that the
    // server is still running: the form keeps its key, so a retry replays a
    // first try that landed (V2, PR-E4 wave 6).
    mayHaveSaved:
      "The connection dropped before an answer came back, so the change may have been saved. Try again, and it will not be saved twice.",
    // 409 IDEMPOTENCY_KEY_REUSE: a retry that changed what it sends after its
    // first try had landed (V2).
    earlierTryLanded:
      "An earlier try of this change went through, and the list now shows it. Check the list before you try again.",
  },
  // The name of each filter a view can hold, keyed by its URL key on the
  // adopting screens (the contracts registry's filter keys). A refusal names
  // the filter with these, since the views layer knows no screen's labels.
  // `views-contract.test.ts` holds every registry filter key to a name here.
  filters: {
    status: "Status",
    facility_id: "Facility",
    work_center_id: "Work centre",
    product_id: "Product",
    due_after: "Due from",
    due_before: "Due to",
    overdue: "Overdue only",
    q: "Search",
    tier: "Price tier",
    owner: "Owner",
    credit_hold: "Credit hold",
    customer: "Customer",
    origin: "Origin",
    off_list: "Off-list lines only",
    created_from: "Created from",
    created_to: "Created to",
    facility: "Facility",
    // The deal list (spec 09 §4.14, P3-E2 PR E2-1).
    stage: "Stage",
    close_from: "Close from",
    close_to: "Close to",
    rotting: "Rotting only",
    needs_next_step: "Needs a next step",
    // The customer book's Tag filter (spec 16 §3.12 item 1, P3-E2 PR E2-2a1).
    tag: "Tag",
    lost_reason: "Lost reason",
    // The task list (spec 09 §4.12, P3-E2 PR E2-1).
    scope: "Whose tasks",
    due: "Due",
    assignee: "Assignee",
  },
  // The server's own field-error message keys (`errors[].message_key`,
  // D-022). The saved-views service emits exactly one, D-305 ruling 31's
  // refusal of a shared customer view whose search holds a contact address.
  validation: {
    contact_address_not_shareable:
      "A shared view can't keep a phone number, chat id or email in its search. Clear the search or keep it private.",
  },
  manage: {
    title: "Manage views",
    close: "Close",
    private: "Private",
    isShared: "Shared",
    rename: "Rename",
    renameLabel: "New name for {{name}}",
    renameSave: "Save name",
    cancel: "Cancel",
    share: "Share",
    unshare: "Stop sharing",
    delete: "Delete",
    deleteConfirm: "Delete {{name}}? This cannot be undone.",
    deleteYes: "Delete view",
    working: "Saving…",
  },
  more: {
    title: "Apply a view",
    close: "Close",
    applied: "Applied",
  },
  columns: {
    title: "Columns",
    close: "Close",
    done: "Done",
    fixed: "Fixed",
    resetWidths: "Reset widths",
    moveUp: "Move {{column}} up",
    moveDown: "Move {{column}} down",
    resize: "Resize {{column}}",
  },
  region: {
    loading: "Loading the list",
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type ViewsDict = Translated<typeof viewsEn>;

// ru is TRANSLATED (views.ru.ts). fr, zh, zh-Hant and hi are machine-translated
// in views.<locale>.ts, which src/locales/<locale>.ts imports directly. The
// claims live in src/i18n-completeness.ts and tools/ci/i18n-parity.ts holds
// every locale to them.
export const viewsRu: ViewsDict = viewsRuDict;
